/*
 * A failed call, read once: the gateway wraps iam's refusal as
 * `{ status: 'error', data: { code, error, request_id } }` (sometimes without
 * the outer `data`), with the gRPC code as a number or a numeric string.
 * Every page's error text starts from this.
 */
export interface ApiError {
    /** gRPC code, when the gateway sent one. */
    code?: number;
    /** HTTP status; undefined when nothing answered. */
    status?: number;
    /** The backend's own words, untrusted: shown only once checked. */
    raw: string;
    requestId?: string;
    /** No answer at all: offline, DNS, CORS, a dropped connection. */
    unreachable: boolean;
}

type Envelope = { data?: Envelope; error?: unknown; message?: unknown; code?: unknown; request_id?: unknown };

export function readApiError(e: unknown): ApiError {
    const res = (e as { response?: { status?: number; data?: Envelope } } | null)?.response;
    const inner = res?.data?.data ?? res?.data;
    const rawCode = inner?.code;
    const code = typeof rawCode === 'number' ? rawCode : typeof rawCode === 'string' && /^\d+$/.test(rawCode) ? Number(rawCode) : undefined;
    const raw = (typeof inner?.error === 'string' && inner.error) || (typeof inner?.message === 'string' && inner.message) || '';
    return {
        code,
        status: res?.status,
        raw,
        requestId: typeof inner?.request_id === 'string' ? inner.request_id : undefined,
        unreachable: !res,
    };
}

/** Worth trying again as it is: nothing answered, or the backend was down or slow. */
export function isTransient(e: unknown): boolean {
    const { code, status, unreachable } = readApiError(e);
    if (unreachable) return true;
    if (code !== undefined) return code === 4 || code === 14;
    return status !== undefined && status >= 502 && status <= 504;
}

/** The account already exists (ALREADY_EXISTS, or a 409 without a code). */
export function alreadyExists(e: unknown): boolean {
    const { code, status } = readApiError(e);
    return code === 6 || (code === undefined && status === 409);
}

/** Signed in as someone the invitation wasn't for (PERMISSION_DENIED, or a 403). */
export function isWrongAccount(e: unknown): boolean {
    const { code, status } = readApiError(e);
    return code === 7 || status === 403;
}

/**
 * The address iam's "existing account, sign in to accept" refusal names
 * ("… sign in as ana@acme.test to accept it"), to name the sign-in. Never shown
 * as such; undefined unless it's plainly one email address.
 */
export function signInAsFrom(e: unknown): string | undefined {
    const m = /sign in as (\S+) to accept/.exec(readApiError(e).raw);
    return m && /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(m[1]) ? m[1] : undefined;
}
